/** Public delivery-feedback page (tokenised link sent after delivery). */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/order-feedback`;

export default function OrderFeedbackPublic() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [rating, setRating] = useState(0);
  const [form, setForm] = useState({ comments: "", issue: "", support_required: false, name: "" });
  const [done, setDone] = useState(null);
  useEffect(() => { axios.get(`${API}/${token}`).then((r) => setCtx(r.data)).catch((e) => setErr(e.response?.data?.detail || "This link is not valid")); }, [token]);
  const submit = async () => {
    if (!rating) return setErr("Please select a rating");
    try { const { data } = await axios.post(`${API}/${token}`, { rating, ...form }); setDone(data); setErr(null); } catch (e) { setErr(e.response?.data?.detail || "Could not submit"); }
  };
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10" data-testid="ofb-root">
      <div className="mx-auto max-w-lg rounded-2xl bg-white p-6 shadow-sm">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-700">SUNTRONIX · Customer Experience</div>
        {err && !ctx && <div className="mt-4 text-rose-700" data-testid="ofb-error">{err}</div>}
        {ctx && (done || ctx.already_submitted) ? (
          <div className="mt-4" data-testid="ofb-thanks"><h1 className="text-xl font-bold">Thank you</h1><p className="mt-2 text-sm text-slate-600">{done?.thank_you || "Feedback for this order has already been recorded. We sincerely appreciate your business."}</p></div>
        ) : ctx && (
          <>
            <h1 className="mt-2 text-xl font-bold" data-testid="ofb-title">How did we do?</h1>
            <p className="mt-1 text-sm text-slate-600">Order <b>{ctx.order_code}</b>{ctx.delivered_date ? ` · delivered ${ctx.delivered_date}` : ""}</p>
            <div className="mt-4 flex gap-1" data-testid="ofb-stars">
              {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} aria-label={`${n} star`} data-testid={`ofb-star-${n}`}><Star className={`h-8 w-8 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}
            </div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name (optional)" className="mt-4" data-testid="ofb-name" />
            <Textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} placeholder="Comments" className="mt-2" data-testid="ofb-comments" />
            <Input value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} placeholder="Any issue with the delivery or product?" className="mt-2" data-testid="ofb-issue" />
            <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.support_required} onChange={(e) => setForm({ ...form, support_required: e.target.checked })} data-testid="ofb-support" />I need support — please contact me</label>
            {err && <div className="mt-2 text-sm text-rose-700" data-testid="ofb-error">{err}</div>}
            <Button className="mt-4 w-full" onClick={submit} data-testid="ofb-submit">Submit feedback</Button>
          </>
        )}
      </div>
    </div>
  );
}
